<template>
  <label :class="[$style['form-input'], {[$style['form-input--error']]: meta.touched && !meta.valid }]">
    <div v-if="label || $attrs.type === 'file'" :id="`${id}-label`" :class="$style['form-input__label']">
      <Icon v-if="$attrs.type === 'file'" name="Camera" />
      {{ label }}
    </div>
    <div v-if="$attrs.type === 'search'" :class="$style['form-input__search-wrap']">
      <input v-model="value" v-bind="$attrs" :class="$style['form-input__input']" :placeholder="placeholder" :aria-invalid="invalid" :aria-describedby="describedBy" :aria-labelledby="label ? `${id}-label` : undefined">
      <button v-if="value" type="button" :class="$style['form-input__search-clear']" aria-label="Wis zoekterm" @click="value = ''">
        <Icon name="X" />
      </button>
    </div>
    <input v-else v-model="value" v-bind="$attrs" :class="$style['form-input__input']" :placeholder="placeholder" :aria-invalid="invalid" :aria-describedby="describedBy" :aria-labelledby="label ? `${id}-label` : undefined">

    <div v-if="hint && !errors.length" :id="`${id}-hint`" :class="$style['form-input__hint']">
      {{ hint }}
    </div>
    <div v-if="errors.length" :id="`${id}-error`" :class="$style['form-input__errors']">
      {{ errors[0] }}
    </div>
  </label>
</template>
<script setup lang="ts">
import { useField } from 'vee-validate'
const props = defineProps<{
  name: string,
  modelValue?: string | number | undefined,
  label?: string,
  placeholder?: string,
  // A short help text under the field, replaced by the error when there is one
  hint?: string
}>()

const { value, errors, meta } = useField(props.name, undefined, { syncVModel: true })

// Screen readers name the field by its label only, and read the error (or the hint) as its description
const id = useId()
const invalid = computed(() => errors.value.length ? 'true' : undefined)
const describedBy = computed(() => {
  if (errors.value.length) { return `${id}-error` }
  return props.hint ? `${id}-hint` : undefined
})
</script>
<style  module>
.form-input {
  width: 100%;
}

.form-input__label {
  font-size: var(--font-size);
  font-weight: var(--font-weight-bold);
  margin-bottom: var(--spacing-s);
}

.form-input__input {
  background-color: transparent;
  border-radius: 999px;
  color: var(--text-base);
  border: 1px solid var(--border-subtle);
  padding: var(--spacing-l) var(--spacing-xl);
  font-size: var(--font-size);
  outline: none;
  width: 100%;
}

/* The public pages: square fields like the photos, amber focus, and errors in a red that reads on black */
:global(:where([data-theme="stage"])) .form-input__input {
  border-radius: 0;
}

.form-input__input:focus {
  border-color: var(--light-color-60);
}

:global(:where([data-theme="stage"])) .form-input__input:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 0.0625rem var(--primary-color);
}

.form-input__hint {
  color: var(--text-subtle);
  font-size: var(--font-size-small);
  padding: var(--spacing) 0;
}

.form-input__errors {
  color: var(--secondary-color-50);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-bold);
  padding: var(--spacing) 0;
}

:global(:where([data-theme="stage"])) .form-input__errors {
  color: var(--stage-error);
}

.form-input--error .form-input__input {
  border-bottom-color: var(--secondary-color-50);
}

:global(:where([data-theme="stage"])) .form-input--error .form-input__input {
  border-color: var(--stage-error);
}

.form-input[type="file"] .form-input__input {
  display: none;
}

.form-input[type="file"] {
  border: 1px solid var(--border-subtle);
  background: var(--opacity-surface);
  border-radius: var(--border-radius);
  cursor: pointer;
  color: var(--color);
  display: inline-flex;
  align-items: center;
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size);
  font-family: var(--font-family);
  padding: var(--spacing-m);
  justify-content: center;
}

.form-input[type="file"] svg {
  vertical-align: text-bottom;
  margin-right: var(--spacing);
}

.form-input[type="color"] .form-input__input {
  padding: 0;
  margin: 0;
  width: 5rem;
  height: 5rem;
}

.form-input[type="color"] .form-input__input:focus {
  border-color: transparent;
}

.form-input[type="color"] ::-webkit-color-swatch-wrapper {
  padding: 0;
}

.form-input[type="color"] ::-webkit-color-swatch {
  border-color: transparent;
  height: 100px;
  width: 100px;
}

.form-input__search-wrap {
  position: relative;
}

.form-input__search-wrap .form-input__input::-webkit-search-cancel-button {
  appearance: none;
}

.form-input__search-clear {
  align-items: center;
  background: none;
  border: none;
  color: var(--text-subtle);
  cursor: pointer;
  display: flex;
  padding: 0;
  position: absolute;
  right: var(--spacing-xl);
  top: 50%;
  transform: translateY(-50%);
}

.form-input__search-wrap .form-input__search-clear svg {
  height: var(--spacing-xl);
  width: var(--spacing-xl);
}

</style>
